import { useCallback, useRef, useState } from "react";
import { http } from "@/lib/api";

export const useRecorder = (onTranscript, onError) => {
  const [recordingIndex, setRecordingIndex] = useState(null);
  const [pending, setPending] = useState({});
  const mr = useRef(null);
  const inflight = useRef([]);

  const upload = useCallback(async (blob, index) => {
    setPending((p) => ({ ...p, [index]: (p[index] || 0) + 1 }));
    try {
      const fd = new FormData();
      const ext = blob.type.includes("mp4") ? "m4a" : "webm";
      fd.append("audio", blob, `answer-${index}.${ext}`);
      const { data } = await http.post("/transcribe", fd);
      if (data.text) onTranscript(index, data.text);
      else onError("Didn't catch that — no speech detected.");
    } catch (e) {
      onError("Transcription failed. You can type your answer instead.");
    } finally {
      setPending((p) => ({ ...p, [index]: p[index] - 1 }));
    }
  }, [onTranscript, onError]);

  const start = useCallback(async (index) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
      const mime = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"].find((t) => window.MediaRecorder?.isTypeSupported?.(t));
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      const chunks = [];
      rec.ondataavailable = (e) => e.data.size && chunks.push(e.data);
      const done = new Promise((resolve) => {
        rec.onstop = () => {
          stream.getTracks().forEach((t) => t.stop());
          const blob = new Blob(chunks, { type: rec.mimeType || "audio/webm" });
          resolve(blob.size > 2000 ? upload(blob, index) : null);
        };
      });
      inflight.current.push(done);
      rec.start(1000);
      mr.current = rec;
      setRecordingIndex(index);
    } catch (e) {
      onError("Microphone unavailable. Allow mic access or type your answer.");
    }
  }, [upload, onError]);

  const stop = useCallback(() => {
    if (mr.current && mr.current.state !== "inactive") mr.current.stop();
    mr.current = null;
    setRecordingIndex(null);
  }, []);

  const flush = useCallback(async () => {
    stop();
    await Promise.all(inflight.current);
  }, [stop]);

  return { recordingIndex, pending, start, stop, flush };
};
